import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Upload } from "lucide-react";
import SEO from "@/components/SEO";

const GOOGLE = "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;1,9..144,300;1,9..144,400&family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=Newsreader:ital,opsz,wght@0,6..72,300;0,6..72,400;1,6..72,300;1,6..72,400&family=Gloock&family=Bodoni+Moda:ital,opsz,wght@0,6..96,400;1,6..96,400&family=DM+Serif+Display:ital@0;1&family=Playfair+Display:ital,wght@0,400;1,400&family=Libre+Caslon+Display&family=Gilda+Display&family=Spectral:ital,wght@0,300;1,300&family=Italiana&family=Syne:wght@500;600&family=Archivo:ital,wdth,wght@0,125,500;1,125,500&display=swap";

const FONTS = [
    { name: "Instrument Serif", note: "Current title font", family: "'Instrument Serif', serif", weight: 400 },
    { name: "Fraunces", note: "Free · Google Fonts", family: "'Fraunces', serif", weight: 300 },
    { name: "Cormorant Garamond", note: "Free · Google Fonts", family: "'Cormorant Garamond', serif", weight: 500 },
    { name: "Newsreader", note: "Free · Google Fonts", family: "'Newsreader', serif", weight: 300 },
    { name: "Gloock", note: "Free · Google Fonts", family: "'Gloock', serif", weight: 400 },
    { name: "Bodoni Moda", note: "Free · Google Fonts", family: "'Bodoni Moda', serif", weight: 400 },
    { name: "DM Serif Display", note: "Free · Google Fonts · bold, high contrast", family: "'DM Serif Display', serif", weight: 400 },
    { name: "Playfair Display", note: "Free · Google Fonts · classic editorial", family: "'Playfair Display', serif", weight: 400 },
    { name: "Libre Caslon Display", note: "Free · Google Fonts · refined, bookish", family: "'Libre Caslon Display', serif", weight: 400 },
    { name: "Gilda Display", note: "Free · Google Fonts · soft, elegant", family: "'Gilda Display', serif", weight: 400 },
    { name: "Spectral", note: "Free · Google Fonts · light, literary", family: "'Spectral', serif", weight: 300 },
    { name: "Italiana", note: "Free · Google Fonts · fashion-house thin", family: "'Italiana', serif", weight: 400 },
    { name: "Syne", note: "Free · Google Fonts · sans, art-gallery modern", family: "'Syne', sans-serif", weight: 600 },
    { name: "Archivo Expanded", note: "Free · Google Fonts · wide sans, architectural", family: "'Archivo', sans-serif", weight: 500, stretch: "125%" },
];

function Specimen({ f, i }) {
    const st = { fontFamily: f.family, fontWeight: f.weight, fontStretch: f.stretch };
    return (
        <section data-testid={`font-specimen-${i}`} className="border-t border-hairline py-14 md:py-20">
            <div className="flex flex-wrap items-baseline justify-between gap-4 mb-10">
                <p className="font-sans text-xl text-forest font-medium">{f.name}</p>
                <p className="mono-label text-moss">{f.note}</p>
            </div>
            <div className="bg-forest text-cream rounded-2xl p-8 md:p-14 mb-6">
                <p style={st} className="text-5xl sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight">
                    Finding potential.<br /><em className="italic text-sage">Building tomorrow.</em>
                </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
                <div className="rounded-2xl bg-surface p-8 md:p-10">
                    <p style={st} className="text-4xl md:text-5xl text-forest leading-[1.04] tracking-tight">Every project <em className="italic">starts with a walk.</em></p>
                </div>
                <div className="rounded-2xl bg-surface p-8 md:p-10">
                    <p style={st} className="text-4xl md:text-5xl text-forest leading-[1.04] tracking-tight">18 homes. One exceptional opportunity <em className="italic">in Sintra.</em></p>
                </div>
            </div>
            <p style={st} className="mt-8 text-3xl text-forest">Built for <em className="italic">Tomorrow.</em> · 5–10% · Verde Capital · Cascais &amp; Sintra</p>
        </section>
    );
}

export default function FontPreview() {
    const [custom, setCustom] = useState([]);

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        for (const file of files) {
            const raw = file.name.replace(/\.[^.]+$/, "");
            const italic = /italic/i.test(raw);
            const base = raw.replace(/[-_ ]?(italic|regular|roman|book)/gi, "").trim() || raw;
            const fam = `Trial-${base.replace(/[^A-Za-z0-9]/g, "")}`;
            try {
                const face = new FontFace(fam, await file.arrayBuffer(), { style: italic ? "italic" : "normal" });
                await face.load();
                document.fonts.add(face);
                setCustom((c) => (c.some((x) => x.fam === fam) ? c : [...c, { fam, name: base, note: "Your trial font · only visible to you", family: `'${fam}', serif`, weight: 400 }]));
            } catch {
                alert(`Could not read ${file.name}. Use .woff2, .woff, .otf or .ttf.`);
            }
        }
    };

    return (
        <div data-testid="font-preview-page" className="bg-cream text-ink min-h-screen">
            <SEO path="/font-preview" title="Title font preview" description="Internal font comparison" noindex />
            <Helmet><link rel="stylesheet" href={GOOGLE} /></Helmet>
            <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
                <img src="/verde2050-logo-green.png" alt="Verde 2050" className="h-8 w-auto mb-12" />
                <p className="mono-label text-moss mb-4">Internal · not linked from the site</p>
                <h1 className="font-sans text-4xl md:text-5xl text-forest font-medium tracking-tight mb-6">Title font preview</h1>
                <p className="text-muted_text max-w-2xl leading-relaxed mb-10">
                    Compare candidate title fonts with real Verde 2050 headlines. To test paid fonts (Canela, GT Sectra, Domaine, Ogg…), download the foundry's free trial and drop the font files below — they load only in your browser and are never uploaded.
                </p>
                <label data-testid="font-upload" className="inline-flex items-center gap-3 rounded-full bg-forest text-cream hover:bg-forest-hover px-7 py-4 text-sm font-medium cursor-pointer transition-colors">
                    <Upload className="h-4 w-4" /> Add trial font files
                    <input type="file" multiple accept=".woff2,.woff,.otf,.ttf" className="hidden" onChange={onFiles} data-testid="font-upload-input" />
                </label>
                <p className="mt-3 text-xs text-muted_text">Tip: add the Regular and Italic files of each font so the italic lines show correctly.</p>
                <div className="mt-16">
                    {[...custom, ...FONTS].map((f, i) => <Specimen key={f.name + i} f={f} i={i} />)}
                </div>
            </div>
        </div>
    );
}
